import React, { useCallback, useEffect, useMemo, useState } from "react";
import { statsAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import Button from "../components/Button";
import Spinner from "../components/Spinner";

import "./SuperAdminStats.css";

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US").format(Number(value || 0));

const isPeriodValue = (value) =>
  value &&
  typeof value === "object" &&
  ["today", "month", "ever"].every((key) =>
    Object.prototype.hasOwnProperty.call(value, key),
  );

const formatStatValue = (value) => {
  if (!isPeriodValue(value)) {
    return formatNumber(value);
  }

  return ["today", "month", "ever"]
    .map((key) => formatNumber(value[key]))
    .join(" | ");
};

const formatDateTime = (value) => {
  if (!value) return "Not loaded yet";
  return new Date(value).toLocaleString();
};

const StatCard = ({
  label,
  value,
  detail,
  tone = "default",
  detailFirst = false,
}) => (
  <article
    className={`superadmin-stats-card superadmin-stats-card-${tone}${
      detailFirst ? " superadmin-stats-card-detail-first" : ""
    }`}
  >
    <span className="superadmin-stats-card-label">{label}</span>
    {detailFirst && detail && (
      <span className="superadmin-stats-card-detail">{detail}</span>
    )}
    <strong className="superadmin-stats-card-value">
      {formatStatValue(value)}
    </strong>
    {!detailFirst && detail && (
      <span className="superadmin-stats-card-detail">{detail}</span>
    )}
  </article>
);

const StatsSection = ({ title, children }) => (
  <section className="superadmin-stats-section">
    <h2>{title}</h2>
    <div className="superadmin-stats-grid">{children}</div>
  </section>
);

const SuperAdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToasts();

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await statsAPI.getOverview();
      setStats(res.data);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to load statistics",
        "error-warning",
      );
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const realUsers = stats?.users?.real || {};
  const communities = stats?.communities || {};
  const content = stats?.content || {};
  const connections = stats?.connections || {};
  const editorialContent = stats?.editorialContent || {};

  const realUserTierDetail = useMemo(() => {
    const byTier = realUsers.byTier || {};
    const entries = Object.entries(byTier);
    if (entries.length === 0) return "No tier data yet";
    return entries
      .map(([tier, count]) => `${tier}: ${formatNumber(count)}`)
      .join(" | ");
  }, [realUsers.byTier]);

  if (loading) return <Spinner />;

  return (
    <div className="form-content-frame superadmin-stats-page">
      <div className="superadmin-stats-header">
        <div>
          <h1>Stats</h1>
          <p>Last generated: {formatDateTime(stats?.generatedAt)}</p>
        </div>
        <div className="superadmin-stats-actions">
          <Button className="button-middle" onClick={loadStats}>
            Refresh
          </Button>
        </div>
      </div>

      <StatsSection title="Real Users">
        <StatCard
          label="Real users"
          value={realUsers.total}
          detail="In Production it excludes editorial, staff/internal, admin, superadmin accounts"
          tone="default"
          detailFirst
        />
        <StatCard label="Active" value={realUsers.active} detail="Email verified, can login" detailFirst />
        <StatCard label="Inactive" value={realUsers.inactive} detail="Blocked, deactivated or unverified" detailFirst />
        <StatCard label="Email unverified" value={realUsers.emailUnverified} detail="From inactive, pending email verification" detailFirst />
        <StatCard label="Public profiles" value={realUsers.publicProfiles} />
        <StatCard
          label="Real users by tier"
          value={realUsers.total}
          detail={realUserTierDetail}
          detailFirst
        />
      </StatsSection>

      <StatsSection title="Editorial Content">
        <StatCard
          label="Lesson opens"
          value={editorialContent.lessonOpens}
          detail={`${formatStatValue(editorialContent.lessons)} editorial lessons`}
          detailFirst
        />
        <StatCard
          label="Notes opens"
          value={editorialContent.notesOpens}
          detail="Clicks into notes from editorial lessons"
          detailFirst
        />
        <StatCard
          label="Flashcards opens"
          value={editorialContent.flashcardsOpens}
          detail="Clicks into flashcards from editorial lessons"
          detailFirst
        />
        {[0, 1, 2].map((index) => {
          const lesson = editorialContent.topLessons?.[index];
          return (
            <StatCard
              key={`editorial-top-${index}`}
              label={`Top ${index + 1}`}
              value={lesson?.opens || 0}
              detail={lesson?.title || "No lesson opens yet"}
              detailFirst
            />
          );
        })}
      </StatsSection>

      <StatsSection title="Communities">
        <StatCard label="Total communities" value={communities.total} detail="Owned by real users" detailFirst />
        <StatCard label="Active" value={communities.active} detail="Not archived, available to members" detailFirst />
        <StatCard label="Archived" value={communities.archived} detail="Frozen by owner, content preserved" detailFirst />
        <StatCard label="Listed" value={communities.listed} detail="Visible in Discover Communities" detailFirst />
        <StatCard
          label="Active memberships"
          value={communities.memberships?.active}
          detail="Members with active status across all communities"
          detailFirst
        />
        <StatCard label="Shared lessons" value={communities.sharedLessons} detail="Lessons shared into communities" detailFirst />
      </StatsSection>

      <StatsSection title="Content">
        <StatCard label="Content items" value={content.total} detail="Lessons + notes + flashcards" detailFirst />
        <StatCard label="Public content" value={content.public} detail="Visible on homepage and shareable" detailFirst />
        <StatCard label="Private content" value={content.private} detail="Only visible to the creator" detailFirst />
        <StatCard label="Total lessons" value={content.lessons} detail="Parent content units" detailFirst />
        <StatCard label="Total notes" value={content.notes} detail="Linked to lessons" detailFirst />
        <StatCard label="Total flashcards" value={content.flashcards} detail="Linked to lessons" detailFirst />
      </StatsSection>

      <StatsSection title="Conversations">
        <StatCard label="Total conversations" value={connections.conversations} detail="Message threads between users" detailFirst />
        <StatCard label="Notes in conversations" value={connections.conversationNotes} detail="Total messages sent in conversations" detailFirst />
        <StatCard label="Notes per conversation" value={connections.notesPerConversation} detail="Average messages per thread" detailFirst />
      </StatsSection>

      <StatsSection title="Connections">
        <StatCard label="Total connections" value={connections.accepted} detail="Accepted connect requests" detailFirst />
        <StatCard label="Notes in connections" value={connections.acceptedConnectionNotes} detail="Total messages in accepted connections" detailFirst />
        <StatCard label="Notes per connection" value={connections.notesPerAccepted} detail="Average messages per connection" detailFirst />
      </StatsSection>

    </div>
  );
};

export default SuperAdminStats;
